import './sidebar.less'

import {m, MediaQuery} from '@codeurs/front'
import {ArrowLinkIcon} from 'assets/inline-icons'
import {Component, Fragment, useEffect, useRef, useState} from 'react'
import AnimateHeight from 'react-animate-height'
import {NavLink} from 'react-router-dom'
import {CategoryItemType, EntryData, SubCategoryType} from 'types'
import {t} from 'util/i18n'
import {isActiveNav, PageLink} from 'util/pagelink'
import {style} from 'util/style'
import {isIE} from 'util/tools'

const styles = style('sidebar')

export class Sidebar extends Component<
	SubCategoryType & {category: EntryData; pageId: number}
> {
	render() {
		const {pageId, category, ...item} = this.props
		if (item.children && item.children.length <= 1) return null
		return (
			<div
				key={'sidebar' + pageId}
				id={'sidebar' + pageId}
				class={styles.mod({ie: isIE()})}
			>
				<MediaQuery
					minWidth={1024}
					view={() => {
						const refBar = useRef(null)
						const refScroll = useRef(null)
						const refFade = useRef(null)
						const refFadeTop = useRef(null)

						const setScrollIndication = () => {
							if (
								!refBar.current ||
								!refScroll.current ||
								!refFade.current ||
								!refFadeTop.current
							)
								return
							const top = refScroll.current.scrollTop
							const height =
								refScroll.current.scrollHeight - refScroll.current.clientHeight
							const scrolled = Math.min(100, (top / height) * 100)
							const fadeHeight = !height ? 0 : 100 - scrolled
							const fadeHeightTop = !height ? 0 : scrolled * 1.5
							refBar.current.style.width = scrolled + '%'
							refFade.current.style.height = fadeHeight + 'px'
							refFadeTop.current.style.height = fadeHeightTop + 'px'
						}

						useEffect(() => {
							setScrollIndication()
						}, [refBar, refScroll, refFade, refFadeTop])

						return (
							<Fragment>
								<div
									ref={refFadeTop}
									class={styles('scrollcontainer-fade').mod('top')}
								/>
								<div class={styles('scrollcontainer-indicator')}>
									<div
										ref={refBar}
										class={styles('scrollcontainer-indicator-bar')}
									/>
								</div>
								<div class={styles('scrollcontainer-outer')}>
									<div
										ref={refScroll}
										class={styles('scrollcontainer-inner')}
										onScroll={setScrollIndication}
									>
										<SidebarItems {...this.props} />
									</div>
								</div>
								<div
									ref={refFade}
									class={styles('scrollcontainer-fade').mod('bottom')}
								/>
							</Fragment>
						)
					}}
				/>
				<MediaQuery
					maxWidth={1023}
					view={() => {
						const [isOpen, setOpen] = useState(false)
						return (
							<div className={styles('togglercontainer')}>
								<a
									className={styles('togglercontainer-toggler')}
									onClick={() => setOpen(!isOpen)}
								>
									{`${category.title} - ${item.title}`}
									<ArrowLinkIcon
										className={styles('togglercontainer-toggler-icon').is({
											open: isOpen
										})}
									/>
								</a>
								<AnimateHeight height={isOpen ? 'auto' : 0}>
									<SidebarItems
										mobile={true}
										{...this.props}
										setOpen={() => setOpen(false)}
									/>
								</AnimateHeight>
							</div>
						)
					}}
				/>
			</div>
		)
	}
}

const SidebarItems: React.FC<
	SubCategoryType & {
		pageId: number
		setOpen?: (open: boolean) => void
		mobile?: boolean
	}
> = ({setOpen, mobile, ...props}) => {
	const {pageId, children, ...item} = props
	return (
		<Fragment>
			{children.length > 0 &&
				children.map(page => (
					<div class={styles('item')}>
						<PageLink
							to={page.url}
							onClick={() => mobile && setOpen(false)}
							exact={page.channel === 'parent_page' && pageId !== page.id}
							class={styles('item-link').is({
								active: isActiveNav(page.url) && page.id === pageId
							})}
						>
							{page.title}
						</PageLink>
						{page.channel === 'parent_page' && isSubpage(page, pageId) && (
							<div class={styles('subitems')}>
								{page.children.map(subpage => (
									<PageLink
										to={subpage.url}
										class={styles('subitem').is({
											active: isActiveNav(subpage.url)
										})}
										onClick={() => mobile && setOpen(false)}
									>
										{subpage.title}
									</PageLink>
								))}
							</div>
						)}
					</div>
				))}
		</Fragment>
	)
}
const isSubpage = (
	item: {id: number; children: Array<CategoryItemType>},
	pageId: number
) => {
	if (pageId === item.id) return true
	return !!item.children.find(c => c.id === pageId)
}
